import { Q, type Collection } from "@nozbe/watermelondb";
import { getOfflineDatabase } from "./database";
import {
  OfflineResource,
  type OfflineResourceSnapshot,
} from "./models/offline-resource";
import { OFFLINE_RESOURCES_TABLE } from "./schema";

const MAX_OFFLINE_PAYLOAD_BYTES = 4 * 1024 * 1024;
const DEFAULT_MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type OfflineApiCacheHit<T> = {
  value: T;
  cachedAt: number;
};

let writeQueue: Promise<void> = Promise.resolve();

function getCollection(): Collection<OfflineResource> | null {
  const database = getOfflineDatabase();
  return database
    ? database.get<OfflineResource>(OFFLINE_RESOURCES_TABLE)
    : null;
}

function enqueueWrite(operation: () => Promise<void>): Promise<void> {
  writeQueue = writeQueue.then(operation, operation).catch((error) => {
    console.warn("SpareLynk offline cache write failed.", error);
  });
  return writeQueue;
}

export async function readOfflineApiResponse<T>(
  cacheKey: string,
): Promise<OfflineApiCacheHit<T> | null> {
  const collection = getCollection();
  if (!collection || !cacheKey) {
    return null;
  }

  try {
    const records = await collection
      .query(Q.where("cache_key", cacheKey))
      .fetch();
    const record = records[0];
    if (!record?.payload) {
      return null;
    }

    return {
      value: JSON.parse(record.payload) as T,
      cachedAt: record.cachedAt,
    };
  } catch (error) {
    console.warn("SpareLynk offline cache read failed.", error);
    return null;
  }
}

export async function writeOfflineApiResponse(
  snapshot: Omit<OfflineResourceSnapshot, "payload" | "cachedAt"> & {
    value: unknown;
  },
): Promise<void> {
  const collection = getCollection();
  if (!collection || !snapshot.cacheKey) {
    return;
  }

  let payload: string;
  try {
    payload = JSON.stringify(snapshot.value);
  } catch {
    return;
  }

  if (
    !payload ||
    new TextEncoder().encode(payload).byteLength > MAX_OFFLINE_PAYLOAD_BYTES
  ) {
    return;
  }

  const resourceSnapshot: OfflineResourceSnapshot = {
    cacheKey: snapshot.cacheKey,
    scope: snapshot.scope,
    path: snapshot.path,
    payload,
    cachedAt: Date.now(),
  };

  await enqueueWrite(async () => {
    const database = getOfflineDatabase();
    if (!database) {
      return;
    }

    const records = await collection
      .query(Q.where("cache_key", snapshot.cacheKey))
      .fetch();
    const existing = records[0];

    await database.write(async () => {
      if (existing) {
        await existing.update((record) => {
          record.applySnapshot(resourceSnapshot);
        });
        return;
      }

      await collection.create((record) => {
        record.applySnapshot(resourceSnapshot);
      });
    });
  });
}

export async function clearOfflineApiResponses(scope?: string): Promise<void> {
  const collection = getCollection();
  const database = getOfflineDatabase();
  if (!collection || !database) {
    return;
  }

  await enqueueWrite(async () => {
    const records = scope
      ? await collection.query(Q.where("scope", scope)).fetch()
      : await collection.query().fetch();

    if (records.length === 0) {
      return;
    }

    await database.write(async () => {
      await database.batch(
        ...records.map((record) => record.prepareDestroyPermanently()),
      );
    });
  });
}

export async function pruneOfflineApiResponses(
  maxAgeMs = DEFAULT_MAX_CACHE_AGE_MS,
): Promise<void> {
  const collection = getCollection();
  const database = getOfflineDatabase();
  if (!collection || !database) {
    return;
  }

  const cutoff = Date.now() - Math.max(0, maxAgeMs);

  await enqueueWrite(async () => {
    const records = await collection
      .query(Q.where("cached_at", Q.lt(cutoff)))
      .fetch();

    if (records.length === 0) {
      return;
    }

    await database.write(async () => {
      await database.batch(
        ...records.map((record) => record.prepareDestroyPermanently()),
      );
    });
  });
}
